/**
 * A minimal i18next-compatible translator: `i18n.t(key, options)`.
 * Both the `i18n` singleton and `useTranslation()`'s `t` satisfy this shape.
 */
export interface ApiErrorTranslator {
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Maps backend error codes (when the API includes one) to translation keys
 * in the `common` namespace's `api_errors` group.
 */
const API_ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {};

/**
 * Maps known raw backend validation messages (DTO/business-rule errors that
 * are not registered i18n keys on the backend, so they come back untranslated
 * regardless of the requested `lang`) to translation keys in the `common`
 * namespace's `api_errors` group. Add new entries here as untranslated
 * backend messages are discovered.
 */
const API_ERROR_MESSAGE_MAPPINGS: {
  key: string;
  match: (message: string) => Record<string, unknown> | null;
}[] = [
  {
    key: 'api_errors.leave_date_in_past',
    match: (message) =>
      message === 'Leave date cannot be in the past' ? {} : null,
  },
  {
    key: 'api_errors.start_date_in_past',
    match: (message) =>
      message === 'Start date cannot be in the past' ? {} : null,
  },
  {
    key: 'api_errors.start_date_after_field',
    match: (message) => {
      const result = message.match(/^Start date cannot be after (.+)$/);
      return result ? { field: result[1] } : null;
    },
  },
  {
    key: 'api_errors.timesheet_date_range_too_long',
    match: (message) => {
      const result = message.match(
        /^Date range must be (\d+) days or fewer with toDate >= fromDate$/
      );
      return result ? { count: Number(result[1]) } : null;
    },
  },
];

/**
 * Maps raw DTO field names (as they appear in backend validation messages)
 * to translation keys for their user-facing label.
 */
const API_ERROR_FIELD_NAME_TRANSLATION_KEYS: Record<string, string> = {
  startDate: 'api_errors.field_names.start_date',
  endDate: 'api_errors.field_names.end_date',
};

/**
 * Translates a raw backend error message into the user's selected language.
 *
 * Backend DTO/business validation errors are usually localized server-side
 * (sent as `common.*` i18n keys and translated using the `lang` query param),
 * but some validators return raw, hardcoded English strings that the backend
 * cannot translate. This looks those up (by error code first, then by exact
 * message match) and translates them via the frontend's i18n instance.
 *
 * If no mapping or translation exists, the original message is returned
 * unchanged so the user never sees an empty string.
 */
export const translateApiErrorMessage = (
  message: string,
  i18n?: ApiErrorTranslator,
  errorCode?: string
): string => {
  if (!message || !i18n) return message;

  const codeKey = errorCode
    ? API_ERROR_CODE_TRANSLATION_KEYS[errorCode]
    : undefined;

  if (codeKey) {
    return i18n.t(codeKey, { ns: 'common', defaultValue: message });
  }

  for (const mapping of API_ERROR_MESSAGE_MAPPINGS) {
    const params = mapping.match(message);
    if (!params) continue;

    if (typeof params.field === 'string') {
      const fieldKey = API_ERROR_FIELD_NAME_TRANSLATION_KEYS[params.field];
      if (fieldKey) {
        params.field = i18n.t(fieldKey, {
          ns: 'common',
          defaultValue: params.field,
        });
      }
    }

    const translated = i18n.t(mapping.key, {
      ns: 'common',
      defaultValue: message,
      ...params,
    });

    if (
      (import.meta as { env?: { DEV?: boolean } }).env?.DEV &&
      translated === message
    ) {
      console.warn(
        `[i18n] Missing translation for API error key "${mapping.key}" (message: "${message}")`
      );
    }

    return translated;
  }

  return message;
};
